/**
 * <facet-results> — filtering, sorting and paging for the collection and search pages.
 *
 * Everything works as plain links and a GET form without this file. With it:
 *   - filter / sort / page changes are fetched through the Section Rendering API and swapped
 *     into the page region by region, the address bar is kept in sync (shareable URLs,
 *     back / forward work)
 *   - the filter panel is a side column on desktop (the toolbar button folds it away) and an
 *     off-canvas drawer on mobile, with focus trapping and scroll locking
 *   - the toolbar re-appears on mobile when the shopper scrolls back up
 *   - the mobile one / two column switch is remembered
 *
 * Attributes on the element
 *   data-section-id        section to request
 *   data-ajax              "true" to update in place, anything else navigates normally
 *   data-auto-hide-soldout "true" to switch on "hide out of stock" with the first filter chosen
 *   data-mobile-cols       written here: "1" | "2" (read by facets.css)
 *
 * Hooks inside
 *   [data-facet-region="name"]  replaced after each request (matched by name)
 *   [data-facets-form]          the filter form
 *   [data-facets-panel]         the filter column / drawer        [data-facets-scroll] its scroller
 *   [data-facets-toggle]        toolbar button                    [data-facets-close] drawer closers
 *   [data-facets-backdrop]      dimmer behind the drawer
 *   a[data-facet-link]          links to load in place (pills, clear all, sort options)
 *   [data-facet-key]            a filter group; its open state survives updates
 *   [data-facet-more]           "show more / less" button for a long value list
 *   [data-results-bar]          toolbar                           [data-results-sentinel] marks its resting place
 *   [data-results-cols="1|2"]   mobile layout buttons
 *   [data-results-anchor]       where to scroll back to after an update
 *
 * Event (bubbles from the element): on:facets:update { url, sectionId }
 */
(() => {
  if (customElements.get('facet-results')) return;

  const DESKTOP_QUERY = '(min-width: 768px)';
  const COLS_STORE = 'susi:results-cols';
  const CHANGE_PAUSE = 450;
  const TYPING_PAUSE = 1200;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), summary';

  const pathAndQuery = (location) => location.pathname + location.search;

  /** Section Rendering responses can leak their own parameters into generated links. */
  const tidyLinks = (scope) => {
    scope.querySelectorAll('a[href*="section_id="], a[href*="sections="]').forEach((link) => {
      try {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.delete('section_id');
        url.searchParams.delete('sections');
        link.setAttribute('href', url.pathname + url.search + url.hash);
      } catch (error) {
        /* leave unusual hrefs alone */
      }
    });
  };

  class FacetResults extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.ajax = this.dataset.ajax === 'true';
      this.autoHideSoldOut = this.dataset.autoHideSoldout === 'true';
      this.availabilityChosen = false;
      this.drawerOpen = false;
      this.opener = null;
      this.controller = null;
      this.formTimer = null;
      this.lastScrollY = window.scrollY;
      this.currentUrl = pathAndQuery(window.location);
      this.desktop = window.matchMedia(DESKTOP_QUERY);

      this.panel = this.querySelector('[data-facets-panel]');
      this.backdrop = this.querySelector('[data-facets-backdrop]');
      this.bar = this.querySelector('[data-results-bar]');
      this.sentinel = this.querySelector('[data-results-sentinel]');

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onScroll = theme.throttle(this.trackScroll.bind(this), 120);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('on:breakpoint-change', this.onViewportChange);
      window.addEventListener('on:debounced-resize', this.onViewportChange);
      if (this.ajax) window.addEventListener('popstate', this.onPopState);

      this.restoreMobileCols();
      this.measure();
      this.syncToggles();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('on:breakpoint-change', this.onViewportChange);
      window.removeEventListener('on:debounced-resize', this.onViewportChange);
      window.removeEventListener('popstate', this.onPopState);
      clearTimeout(this.formTimer);
      if (this.controller) this.controller.abort();
      if (this.drawerOpen) this.closeDrawer({ restoreFocus: false });
    }

    get form() {
      return this.querySelector('[data-facets-form]');
    }

    /* -------------------------------------------------------------------- */
    /* Events                                                                */
    /* -------------------------------------------------------------------- */

    onClick(event) {
      const target = event.target;

      const toggle = target.closest('[data-facets-toggle]');
      if (toggle) {
        event.preventDefault();
        this.togglePanel(toggle);
        return;
      }

      if (target.closest('[data-facets-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const colsButton = target.closest('[data-results-cols]');
      if (colsButton) {
        this.setMobileCols(colsButton.dataset.resultsCols, true);
        return;
      }

      const moreButton = target.closest('[data-facet-more]');
      if (moreButton) {
        const group = moreButton.closest('[data-facet-key]');
        this.setExpanded(group, moreButton.getAttribute('aria-expanded') !== 'true');
        return;
      }

      if (!this.ajax) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = target.closest('a[data-facet-link], [data-facet-region="pagination"] a[href]');
      if (!link || !this.contains(link) || link.target === '_blank') return;

      event.preventDefault();
      const isPaging = !link.hasAttribute('data-facet-link');
      this.load(link.href, { focus: isPaging ? 'results' : 'keep' });
    }

    onChange(event) {
      const form = this.form;
      if (!form || !form.contains(event.target)) return;

      this.applyAutoAvailability(event.target, form);

      if (!this.ajax) {
        window.location.assign(this.urlFromForm(form));
        return;
      }

      // A newer choice makes whatever is on its way stale.
      this.cancelRequest();
      this.setBusy(true);
      this.queueFormLoad(CHANGE_PAUSE);
    }

    /**
     * Typing in a price field or dragging a price thumb while an update is pending: hold the
     * update back until the shopper pauses, so fresh markup never replaces a control in use.
     */
    onInput(event) {
      if (!this.ajax) return;
      const form = this.form;
      if (!form || !form.contains(event.target)) return;
      if (event.target.type !== 'text' && event.target.type !== 'range') return;
      if (!this.formTimer && !this.controller) return;
      this.cancelRequest();
      this.queueFormLoad(TYPING_PAUSE);
    }

    queueFormLoad(delay) {
      clearTimeout(this.formTimer);
      this.formTimer = setTimeout(() => {
        this.formTimer = null;
        const form = this.form;
        if (form) this.load(this.urlFromForm(form), { focus: 'keep' });
      }, delay);
    }

    cancelRequest() {
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
    }

    onSubmit(event) {
      const form = event.target;
      if (!form.matches('[data-facets-form]')) return;
      event.preventDefault();
      const url = this.urlFromForm(form);
      if (this.ajax) this.load(url, { focus: 'keep' });
      else window.location.assign(url);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.drawerOpen) {
        event.preventDefault();
        this.closeDrawer();
      }
    }

    onPopState() {
      if (pathAndQuery(window.location) === this.currentUrl) return;
      this.load(window.location.href, { push: false, focus: 'none', scroll: false });
    }

    onViewportChange() {
      if (this.desktop.matches && this.drawerOpen) this.closeDrawer({ restoreFocus: false });
      this.measure();
      this.syncToggles();
      this.trackScroll();
    }

    /* -------------------------------------------------------------------- */
    /* Building the address                                                  */
    /* -------------------------------------------------------------------- */

    /**
     * Current address with the form's fields laid over it. Search terms, the search type and
     * anything else unrelated to filtering are carried over untouched; the page number is not.
     */
    urlFromForm(form) {
      const params = new URLSearchParams(window.location.search);
      Array.from(params.keys()).forEach((key) => {
        if (key === 'page' || key.startsWith('filter.')) params.delete(key);
      });

      const data = new FormData(form);
      new Set(Array.from(data.keys())).forEach((key) => params.delete(key));
      data.forEach((value, key) => {
        if (typeof value === 'string' && value !== '') params.append(key, value);
      });

      const query = params.toString();
      return window.location.pathname + (query ? `?${query}` : '');
    }

    /** Optional behaviour: the first filter a shopper picks also hides sold out products. */
    applyAutoAvailability(input, form) {
      if (!this.autoHideSoldOut) return;
      if (input.name === 'filter.v.availability') {
        this.availabilityChosen = true;
        return;
      }
      if (this.availabilityChosen) return;
      const picked = (input.type === 'checkbox' && input.checked) || (input.type === 'text' && input.value !== '');
      if (!picked) return;
      const hide = form.querySelector('input[name="filter.v.availability"][value="1"]');
      if (hide) hide.checked = true;
    }

    /* -------------------------------------------------------------------- */
    /* Loading + swapping                                                    */
    /* -------------------------------------------------------------------- */

    /**
     * @param {string} url - Page to show.
     * @param {object} [options]
     * @param {boolean} [options.push=true] - Add a history entry.
     * @param {'keep'|'results'|'none'} [options.focus='keep'] - Where focus goes afterwards.
     * @param {boolean} [options.scroll=true] - Bring the top of the results back into view.
     */
    async load(url, options = {}) {
      const { push = true, focus = 'keep', scroll = true } = options;
      const target = new URL(url, window.location.origin);
      if (target.origin !== window.location.origin || !this.sectionId) {
        window.location.assign(target.toString());
        return;
      }

      clearTimeout(this.formTimer);
      this.formTimer = null;
      this.cancelRequest();
      const controller = new AbortController();
      this.controller = controller;
      this.setBusy(true);

      const request = new URL(target.toString());
      request.searchParams.set('section_id', this.sectionId);

      try {
        const doc = await theme.fetchDocument(request.toString(), { signal: controller.signal });
        if (controller !== this.controller) return;

        const incoming = doc.querySelector('facet-results');
        if (!incoming) throw new Error('Section response has no results');

        const address = target.pathname + target.search;
        if (push && address !== this.currentUrl) window.history.pushState({ facetResults: this.sectionId }, '', address);
        this.currentUrl = pathAndQuery(window.location);

        this.swap(incoming, focus);
        this.controller = null;
        this.setBusy(false);
        if (scroll) this.revealTop();
        theme.emit('on:facets:update', { url: target.toString(), sectionId: this.sectionId }, this);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        // Anything unexpected: fall back to a normal page load of the same address.
        window.location.assign(target.toString());
      }
    }

    swap(incoming, focus) {
      const active = document.activeElement;
      const hadFocus = Boolean(active && active !== document.body && this.contains(active));
      const activeId = hadFocus ? active.id : '';
      const activeRegion = hadFocus ? active.closest('[data-facet-region]') : null;
      const scroller = this.querySelector('[data-facets-scroll]');
      const offsetBefore = hadFocus && scroller && scroller.contains(active) ? active.getBoundingClientRect().top : null;
      const groups = this.readGroups();

      this.querySelectorAll('[data-facet-region]').forEach((region) => {
        const match = incoming.querySelector(`[data-facet-region="${region.dataset.facetRegion}"]`);
        region.innerHTML = match ? match.innerHTML : '';
        tidyLinks(region);
      });

      this.writeGroups(groups);
      this.syncColsButtons();
      if (theme.animations && typeof theme.animations.refresh === 'function') theme.animations.refresh(this);

      if (focus === 'none') return;

      if (focus === 'results') {
        const results = this.querySelector('[data-facet-region="results"]');
        if (results) results.focus({ preventScroll: true });
        return;
      }

      if (!hadFocus) return;
      const same = activeId ? document.getElementById(activeId) : null;
      if (same && this.contains(same) && !same.disabled) {
        same.focus({ preventScroll: true });
        // Pills appearing above the list would otherwise push the control away from the finger.
        if (offsetBefore !== null) scroller.scrollTop += same.getBoundingClientRect().top - offsetBefore;
        return;
      }

      // The control is gone (a removed pill, "clear all"…): stay as close as possible.
      const fallback =
        (activeRegion && activeRegion.isConnected && activeRegion.querySelector(FOCUSABLE)) ||
        (this.drawerOpen && this.panel) ||
        this.querySelector('[data-facet-region="results"]');
      if (fallback) {
        if (!fallback.matches(FOCUSABLE) && !fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
        fallback.focus({ preventScroll: true });
      }
    }

    /** Remembers which groups are open and which long lists are expanded. */
    readGroups() {
      const state = {};
      this.querySelectorAll('[data-facet-key]').forEach((group) => {
        const details = group.querySelector('details');
        const more = group.querySelector('[data-facet-more]');
        state[group.dataset.facetKey] = {
          open: details ? details.open : null,
          expanded: more ? more.getAttribute('aria-expanded') === 'true' : null
        };
      });
      return state;
    }

    writeGroups(state) {
      this.querySelectorAll('[data-facet-key]').forEach((group) => {
        const saved = state[group.dataset.facetKey];
        if (!saved) return;
        const details = group.querySelector('details');
        if (details && saved.open !== null && details.open !== saved.open) details.open = saved.open;
        if (saved.expanded) this.setExpanded(group, true);
      });
    }

    setExpanded(group, expanded) {
      if (!group) return;
      group.classList.toggle('is-expanded', expanded);
      const button = group.querySelector('[data-facet-more]');
      if (button) button.setAttribute('aria-expanded', String(expanded));
    }

    setBusy(busy) {
      this.classList.toggle('is-loading', busy);
      const results = this.querySelector('[data-facet-region="results"]');
      if (results) results.setAttribute('aria-busy', String(busy));
    }

    /** Scrolls up only when the start of the results has left the viewport. */
    revealTop() {
      const anchor = this.querySelector('[data-results-anchor]') || this;
      if (anchor.getBoundingClientRect().top < theme.stickyHeaderHeight()) theme.scrollToElement(anchor, 0);
    }

    /* -------------------------------------------------------------------- */
    /* Filter panel: column on desktop, drawer on mobile                     */
    /* -------------------------------------------------------------------- */

    togglePanel(opener) {
      if (this.desktop.matches) {
        this.classList.toggle('is-collapsed');
        this.syncToggles();
      } else if (this.drawerOpen) {
        this.closeDrawer();
      } else {
        this.openDrawer(opener);
      }
    }

    openDrawer(opener) {
      if (this.drawerOpen || !this.panel) return;
      this.drawerOpen = true;
      this.opener = opener || null;
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      this.classList.add('is-drawer-open');
      if (this.backdrop) this.backdrop.classList.add('is-visible');
      theme.lockScroll();
      theme.trapFocus(this.panel, this.panel.querySelector('[data-facets-close]') || undefined);
      this.syncToggles();
    }

    closeDrawer(options = {}) {
      if (!this.drawerOpen) return;
      const { restoreFocus = true } = options;
      this.drawerOpen = false;
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      this.classList.remove('is-drawer-open');
      if (this.backdrop) this.backdrop.classList.remove('is-visible');
      theme.unlockScroll();
      theme.releaseFocus(restoreFocus && this.opener && this.opener.isConnected ? this.opener : undefined);
      this.opener = null;
      this.syncToggles();
    }

    syncToggles() {
      const expanded = this.desktop.matches ? !this.classList.contains('is-collapsed') : this.drawerOpen;
      this.querySelectorAll('[data-facets-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(expanded));
      });
    }

    /* -------------------------------------------------------------------- */
    /* Toolbar + layout                                                      */
    /* -------------------------------------------------------------------- */

    /** Publishes the sticky header height for the sticky column and the mobile toolbar. */
    measure() {
      this.style.setProperty('--results-sticky-top', `${theme.stickyHeaderHeight()}px`);
    }

    /** Mobile: the toolbar tucks away while scrolling down and returns on the way up. */
    trackScroll() {
      if (!this.bar || !this.sentinel) return;
      const y = window.scrollY;
      const delta = y - this.lastScrollY;
      if (this.desktop.matches) {
        this.bar.classList.remove('is-stuck', 'is-tucked');
        this.lastScrollY = y;
        return;
      }
      if (Math.abs(delta) < 6) return;
      this.lastScrollY = y;

      const offset = theme.stickyHeaderHeight();
      this.style.setProperty('--results-sticky-top', `${offset}px`);
      const stuck = this.sentinel.getBoundingClientRect().top < offset;
      this.bar.classList.toggle('is-stuck', stuck);
      this.bar.classList.toggle('is-tucked', stuck && delta > 0 && !this.drawerOpen);
    }

    setMobileCols(cols, remember) {
      if (cols !== '1' && cols !== '2') return;
      this.dataset.mobileCols = cols;
      this.syncColsButtons();
      if (!remember) return;
      try {
        window.localStorage.setItem(COLS_STORE, cols);
      } catch (error) {
        /* private mode: the choice just lasts for this page */
      }
    }

    restoreMobileCols() {
      if (!this.querySelector('[data-results-cols]')) return;
      let stored = null;
      try {
        stored = window.localStorage.getItem(COLS_STORE);
      } catch (error) {
        stored = null;
      }
      if (stored) this.setMobileCols(stored, false);
      else this.syncColsButtons();
    }

    syncColsButtons() {
      const current = this.dataset.mobileCols;
      if (!current) return;
      this.querySelectorAll('[data-results-cols]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.resultsCols === current));
      });
    }
  }

  customElements.define('facet-results', FacetResults);
})();
